/** Tiny DOM builder: no framework, text is always set via textContent (no HTML injection). */
import { ApiHttpError, NetworkError } from "./staff-api-client";
import { strings } from "./strings";

type Child = Node | string | number | null | undefined | false;
type Props = Record<string, string | number | boolean | EventListener | undefined>;

export function h<K extends keyof HTMLElementTagNameMap>(tag: K, props: Props = {}, ...children: Child[]): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [key, value] of Object.entries(props)) {
    if (value === undefined || value === false) continue;
    if (key.startsWith("on") && typeof value === "function") {
      el.addEventListener(key.slice(2).toLowerCase(), value);
    } else if (key === "className") {
      el.className = String(value);
    } else if (key in el && typeof value !== "string") {
      (el as unknown as Record<string, unknown>)[key] = value;
    } else {
      el.setAttribute(key, value === true ? "" : String(value));
    }
  }
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    el.append(child instanceof Node ? child : String(child));
  }
  return el;
}

/** Labelled form field wrapper. */
export function field(label: string, control: HTMLElement): HTMLLabelElement {
  return h("label", { className: "field" }, h("span", { className: "field-label" }, label), control);
}

/** Czech message for an API/network error. */
export function errorMessage(error: unknown): string {
  if (error instanceof NetworkError) return strings.errorNetwork;
  if (error instanceof ApiHttpError) return strings.errorByCode[error.body.code] ?? error.body.message ?? strings.errorGeneric;
  return strings.errorGeneric;
}

export function errorBox(): { el: HTMLParagraphElement; show(message: string | null): void } {
  const el = h("p", { className: "error", role: "alert", hidden: true });
  return {
    el,
    show(message) {
      el.textContent = message ?? "";
      el.hidden = !message;
    },
  };
}
